import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import useModel3DViewer from "../../../../Customiz3r/Editor3D/useModel3DViewer.js";
import axios from "axios";
import ModelViewerShell from "../../../../../components/Viewer3D/ModelViewerShell.jsx";
import {
    ArrowLeftIcon,
    PencilSquareIcon,
    ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import CustomOrderRequestModal from "../../../../Customiz3r/components/CustomOrderRequestModal.jsx";
import { editorPathForId, editorPathForModel } from "../../../../../products/registry";

const Mi3DViewer = () => {
    const { modelId } = useParams();
    const navigate = useNavigate();

    const [modelMeta, setModelMeta] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [requestOpen, setRequestOpen] = useState(false);

    // Con el modelo cargado se va a su editor (una caja de luz abre el suyo, no
    // el del llavero); mientras llega, la ruta por id sirve de puente y el
    // propio Customizer redirige si el producto no coincide.
    const editorPath = modelMeta ? editorPathForModel(modelMeta) : editorPathForId(modelId);

    // El GLB puede no existir todavía (modelo nunca generado en 3D): se
    // regenera una vez y se reintenta la carga. Se resetea en cada cambio de
    // modelo para no "recordar" un intento de un modelo distinto.
    const regenerationTriedRef = useRef(false);
    useEffect(() => {
        regenerationTriedRef.current = false;
    }, [modelId]);

    const handleLoadError = useCallback((err, { retry, setError, setLoading }) => {
        if (!regenerationTriedRef.current) {
            regenerationTriedRef.current = true;
            console.warn('GLB no disponible, generando el modelo 3D:', err);
            setIsGenerating(true);
            setLoading(true);

            // Sin form2d_data el backend usa los parámetros guardados.
            axios.post(`customized-keyrings/${modelId}/regenerate-3d/`, {})
                .then(() => {
                    setIsGenerating(false);
                    retry();
                })
                .catch((genErr) => {
                    console.error('Error generando el modelo 3D:', genErr);
                    setIsGenerating(false);
                    setError(genErr?.response?.data?.detail || 'No se pudo generar el archivo 3D del modelo.');
                    setLoading(false);
                });
            return;
        }

        console.error('Error cargando modelo:', err);
        setError(`No se pudo cargar el modelo: ${err.message || 'Error desconocido'}`);
        setLoading(false);
    }, [modelId]);

    const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
    const accessToken = localStorage.getItem('access_token');
    // Memoizado: un objeto nuevo en cada render haría que el useEffect del
    // hook (que lo lleva en las dependencias) reinicializase todo el visor
    // — escena, cámara y carga del GLB — en cada re-render de esta página.
    const requestHeaders = useMemo(
        () => (accessToken ? { 'Authorization': `Bearer ${accessToken}` } : undefined),
        [accessToken]
    );
    const viewer = useModel3DViewer({
        glbUrl: modelId ? `${API_BASE_URL}customized-keyrings/${modelId}/glb/` : null,
        requestHeaders,
        bottomLight: true,
        onLoadError: handleLoadError,
    });

    // ── Fetch model metadata ─────────────────────────────────────────────────
    useEffect(() => {
        if (!modelId) return;
        axios.get(`customized-keyrings/${modelId}/`)
            .then(res => setModelMeta(res.data))
            .catch(err => console.warn('No se pudo obtener metadatos del modelo:', err));
    }, [modelId]);

    return (
        <ModelViewerShell
            viewer={viewer}
            subtitle="Visor 3D"
            loadingTitle={isGenerating ? 'Generando modelo 3D…' : 'Cargando modelo 3D'}
            loadingMessage={isGenerating
                ? 'El modelo aún no tenía archivo 3D. Estamos construyéndolo, puede tardar unos segundos.'
                : undefined}
            errorBack={{ label: 'Volver atrás', onClick: () => navigate(-1) }}
            back={{ label: 'Atrás', tip: 'Volver a la página anterior', icon: ArrowLeftIcon, onClick: () => navigate(-1) }}
            modelName={modelMeta?.name || `Modelo #${modelId}`}
            formData={modelMeta?.form2d_data}
            canEditColors
            actions={[
                { key: 'edit', icon: PencilSquareIcon, label: 'Editar en Customiz3r', mobileLabel: 'Editar', tip: 'Editar el diseño en Customiz3r', onClick: () => navigate(editorPath) },
                { key: 'order', icon: ClipboardDocumentCheckIcon, label: 'Pedir este diseño', mobileLabel: 'Pedir', tip: 'Pedir que te fabriquemos este diseño', onClick: () => setRequestOpen(true), isPrimary: true },
            ]}
        >
            {requestOpen && (
                <CustomOrderRequestModal
                    // Si los metadatos aún no llegaron, el modal funciona igual:
                    // sin svg_2d cae al campo de color manual.
                    keyring={modelMeta ?? { id: Number(modelId) }}
                    onClose={() => setRequestOpen(false)}
                />
            )}
        </ModelViewerShell>
    );
};

export default Mi3DViewer;
